/**
 * Feedback widget — a floating button + modal for customers and partners to
 * send feedback, bug reports, or wishes straight from the dashboard.
 *
 * Auto-captures the current page (pathname + hash) so the team sees WHERE the
 * user was. Feedback is stored server-side and emailed to the admin.
 */
import React, { useState } from 'react';
import { MessageSquarePlus, X, Loader2, CheckCircle2, Send } from 'lucide-react';
import { useAetherAuth } from './AetherAuthContext';

const CATEGORIES = [
  { id: 'feedback', label: 'Feedback' },
  { id: 'bug', label: 'Fehler' },
  { id: 'wish', label: 'Wunsch' },
];

export default function FeedbackWidget({ variant = 'floating' }) {
  const { user } = useAetherAuth();
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState('feedback');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const close = () => {
    setOpen(false);
    // reset after the close animation so the form doesn't flash while fading
    setTimeout(() => { setSent(false); setErr(null); setMsg(''); setCat('feedback'); }, 200);
  };

  // Geht an DENSELBEN Posteingang wie die Meldungen aus Plugin und Installer:
  // den Anfragen-Reiter. Vorher landete das hier in einer eigenen Sammlung und
  // schickte nur eine Mail an David — der Absender hat nie wieder etwas gehoert,
  // und im Admin war davon nichts zu sehen.
  const submit = async () => {
    if (!msg.trim()) return;
    setBusy(true); setErr(null);
    try {
      // Kontext gehoert an die Meldung, nicht in eine Rueckfrage. Dieselben
      // Angaben, die auch das Plugin mitschickt.
      const kontext = [
        `Seite: ${window.location.pathname}${window.location.hash || ''}`,
        `Rolle: ${user?.role || 'kunde'}`,
        user?.customer_number ? `Kundennummer: ${user.customer_number}` : null,
        `Browser: ${navigator.userAgent}`,
        `Fenster: ${window.innerWidth}×${window.innerHeight}`,
        '',
        msg.trim(),
      ].filter(Boolean).join('\n');

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: user?.name || (user?.email || '').split('@')[0] || 'Portal',
          email: user?.email || '',
          message: kontext,
          reason: cat === 'bug' ? 'Fehlermeldung' : cat === 'wish' ? 'Wunsch' : 'Feedback',
          source: 'portal',
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Senden fehlgeschlagen');
      setSent(true); setMsg('');
    } catch (e) {
      setErr(e.message || 'Senden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && variant === 'inline' && (
        // Sitzt in der Kopfzeile bei Tour/Konto/Kunde — gleiche Pillen-Optik.
        <button
          onClick={() => setOpen(true)}
          className="px-3 py-1.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition inline-flex items-center gap-1.5"
          title="Feedback geben"
        >
          <MessageSquarePlus size={11} className="text-orange-400" /> Feedback
        </button>
      )}

      {!open && variant === 'floating' && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 left-5 z-[150] inline-flex items-center gap-2 px-4 py-2.5 rounded-full monolith-glass text-white/70 hover:text-white shadow-lg transition hover:scale-[1.03]"
          title="Feedback geben"
        >
          <MessageSquarePlus size={16} className="text-orange-400" />
          <span className="font-mono text-[10px] uppercase tracking-widest">Feedback</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[190] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Feedback geben">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
          <div className="relative monolith-heavy w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-base font-medium">Feedback geben</h3>
              <button onClick={close} className="text-white/40 hover:text-white" title="Schließen"><X size={16} /></button>
            </div>

            {sent ? (
              <div className="py-6 text-center">
                <CheckCircle2 size={28} className="text-emerald-400 mx-auto mb-3" />
                <p className="text-white text-sm">Danke für dein Feedback!</p>
                <p className="text-white/50 text-xs mt-1">Wir schauen es uns an.</p>
                <button onClick={close} className="mt-4 px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-black" style={{ background: 'var(--primary,#F97316)' }}>Schließen</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 mb-3">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCat(c.id)}
                      className={`px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-widest transition ${cat === c.id ? 'bg-white/15 text-white' : 'bg-white/5 text-white/40 hover:text-white/70'}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder="Was möchtest du uns mitteilen?"
                  rows={4}
                  maxLength={4000}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25 placeholder:text-white/25 resize-none aether-scrollbar"
                />
                {err && <p className="mt-2 text-xs text-red-300">{err}</p>}
                <button
                  onClick={submit}
                  disabled={busy || !msg.trim()}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest text-black transition hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100"
                  style={{ background: 'var(--primary,#F97316)' }}
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Senden
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
